import { Injectable, inject } from '@angular/core';
import { DataConnect as AngularDataConnect } from '@angular/fire/data-connect';
import {
  ContentType,
  GetContentListData,
  getContentList,
} from '@dataconnect/copilot';
import { DataConnect } from 'firebase/data-connect';
import { sortBy } from 'lodash-es';

interface CacheEntry {
  data: GetContentListData['contents'];
  expiresAt: number;
}

@Injectable({ providedIn: 'root' })
export class ContentCacheService {
  private readonly dc = inject(AngularDataConnect) as unknown as DataConnect;
  private readonly ttlMs = 60 * 60 * 1000;
  private readonly cache = new Map<string, CacheEntry>();

  async getContent(contentType: ContentType, organizationIds: string[]): Promise<GetContentListData['contents']> {
    const normalizedOrganizationIds = Array.from(new Set(organizationIds.filter(Boolean)));

    if (normalizedOrganizationIds.length === 0) {
      return [];
    }

    const contentByOrganization = await Promise.all(
      normalizedOrganizationIds.map((organizationId) => this.getOrFetchOrganizationContent(organizationId, contentType))
    );

    const dedupedById = new Map<string, GetContentListData['contents'][0]>();
    for (const contentList of contentByOrganization) {
      for (const content of contentList) {
        dedupedById.set(content.id, content);
      }
    }

    return sortBy(Array.from(dedupedById.values()), 'title');
  }

  clearCache(organizationId?: string): void {
    if (!organizationId) {
      this.cache.clear();
      return;
    }

    for (const key of this.cache.keys()) {
      if (key.startsWith(`${organizationId}:`)) {
        this.cache.delete(key);
      }
    }
  }

  private async getOrFetchOrganizationContent(
    organizationId: string,
    contentType: ContentType
  ): Promise<GetContentListData['contents']> {
    const cacheKey = this.getCacheKey(organizationId, contentType);
    const cached = this.cache.get(cacheKey);

    if (cached && cached.expiresAt > Date.now()) {
      return cached.data;
    }

    const response = await getContentList(this.dc, {
      organizationId,
      type: contentType,
    });

    const data = response.data.contents ?? [];
    this.cache.set(cacheKey, {
      data,
      expiresAt: Date.now() + this.ttlMs,
    });

    return data;
  }

  private getCacheKey(organizationId: string, contentType: ContentType): string {
    return `${organizationId}:${contentType}`;
  }
}
